import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ValidateImportPerformanceResultDocument,
    type ValidateImportPerformanceResultMutation,
} from '../../shared/bulk/BulkModification.api';
import client from '@/apolloClient';
import type { PerformanceResultType } from '@/apolloClient.types';
import { error as showErrorNotification } from '@/utils/notifications/actions';

export interface ValidationSummaryUi {
    readyCount: number;
    errorCount: number;
    errors: { row: number; field: string; description: string }[];
}

const useValidateImport = () => {
    const { t } = useTranslation('performanceResult');

    const validate = useCallback(
        async (resultType: PerformanceResultType, file: File) => {
            const { data } = await client.mutate<ValidateImportPerformanceResultMutation>({
                mutation: ValidateImportPerformanceResultDocument,
                variables: { resultType, file },
            });

            if (data?.validateImportPerformanceResult?.__typename === 'ErrorResponse') {
                const errorResponse = data.validateImportPerformanceResult;
                const specificErrorField = errorResponse.fields?.find(f => f.message);
                const errorMessage = specificErrorField?.message || t('bulk.validateImportPerformanceResultErrorDesc');
                showErrorNotification({
                    message: t('bulk.validateImportPerformanceResultErrorTitle'),
                    description: errorMessage,
                });
                return null;
            }

            const result = data?.validateImportPerformanceResult?.result;
            if (!result) {
                return null;
            }

            const readyCount = (result.updateCount ?? 0) + (result.createNewCount ?? 0);
            const errorCount = result.failureCount ?? 0;
            const errors = (result.errors || []).map(e => ({
                row: e.rowNumber || 0,
                field: e.field || '',
                description: e.message || '',
            }));
            const summary: ValidationSummaryUi = { readyCount, errorCount, errors };
            return summary;
        },
        [t]
    );

    return { validate };
};

export default useValidateImport;
